import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Building2, User as UserIcon, Briefcase, Box, Database, Save, Trash2,
  GripVertical, Plus, Pencil, Lock, AlertTriangle, Link2, ArrowRight,
} from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { apiRequest } from '../../config/api';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import FieldDefinitionModal from './FieldDefinitionModal';

const ICON_MAP = {
  'building-2': Building2,
  'user': UserIcon,
  'handshake': Briefcase,
  'box': Box,
};

const FIELD_TYPE_LABELS = {
  text: 'Text',
  url: 'URL',
  number: 'Number',
  date: 'Date',
  select: 'Select',
  multi_select: 'Multi Select',
  boolean: 'Boolean',
  object_relationship: 'Relationship',
  user: 'User',
};

const FIELD_TYPE_COLORS = {
  text: 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
  url: 'bg-violet-50 text-violet-600 dark:bg-violet-900/30 dark:text-violet-400',
  number: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400',
  date: 'bg-orange-50 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400',
  select: 'bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400',
  multi_select: 'bg-pink-50 text-pink-600 dark:bg-pink-900/30 dark:text-pink-400',
  boolean: 'bg-teal-50 text-teal-600 dark:bg-teal-900/30 dark:text-teal-400',
  object_relationship: 'bg-indigo-50 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400',
  user: 'bg-cyan-50 text-cyan-600 dark:bg-cyan-900/30 dark:text-cyan-400',
};


function SortableFieldRow({ field, isSystem, onEdit, onDelete, objectDefinitions }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id, disabled: isSystem });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const relatedObjectName = field.field_type === 'object_relationship' && field.options?.related_object_definition_id
    ? objectDefinitions?.find(od => od.id === field.options.related_object_definition_id)?.name
    : null;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 px-4 py-3 border-b border-gray-50 dark:border-gray-700/50 last:border-b-0 group transition-colors ${
        isDragging ? 'bg-gray-50 dark:bg-gray-700/50 rounded-lg shadow-sm' : 'hover:bg-gray-50/50 dark:hover:bg-gray-700/20'
      } ${field.is_system ? 'opacity-70' : ''}`}
    >
      <div
        {...attributes}
        {...listeners}
        className={`flex-shrink-0 ${field.is_system ? 'cursor-not-allowed opacity-30' : 'cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500 dark:text-gray-600 dark:hover:text-gray-400'}`}
      >
        <GripVertical className="w-4 h-4" />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
            {field.name}
          </span>
          {field.is_system && (
            <Lock className="w-3 h-3 text-gray-400 dark:text-gray-500 flex-shrink-0" />
          )}
          {field.is_required && (
            <span className="text-[10px] font-medium text-rose-500 dark:text-rose-400">Required</span>
          )}
        </div>
        <span className="text-xs text-gray-400 dark:text-gray-500">{field.slug}</span>
      </div>

      <div className="flex items-center gap-2 flex-shrink-0">
        <span className={`px-2 py-0.5 text-[11px] font-medium rounded-full ${FIELD_TYPE_COLORS[field.field_type] || 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'}`}>
          {FIELD_TYPE_LABELS[field.field_type] || field.field_type}
        </span>
        {relatedObjectName && (
          <span className="text-[11px] text-gray-400 dark:text-gray-500">
            → {relatedObjectName}
          </span>
        )}
      </div>

      {!field.is_system && (
        <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={() => onEdit(field)}
            className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            title="Edit field"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => onDelete(field)}
            className="p-1.5 rounded-md text-gray-400 hover:text-rose-600 dark:hover:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-colors"
            title="Delete field"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}

export default function ObjectDefinitionSettings({ objectDefinitionId, objectDefinitions, onObjectDeleted, onObjectUpdated }) {
  const { showSuccess, showError } = useToast();
  const { organization } = useAuth();
  const [objectDef, setObjectDef] = useState(null);
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', icon: '', category: 'CRM', slug_plural: '' });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [fieldModal, setFieldModal] = useState({ open: false, field: null });
  const [deleteFieldConfirm, setDeleteFieldConfirm] = useState(null);
  const [relationships, setRelationships] = useState([]);
  const [showAddRelationship, setShowAddRelationship] = useState(false);
  const [deleteRelConfirm, setDeleteRelConfirm] = useState(null);
  const nameInputRef = useRef(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const fetchObjectDefinition = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiRequest(`/crm/objects/${objectDefinitionId}`);
      if (response.ok) {
        const data = await response.json();
        setObjectDef(data);
        setFields(data.field_definitions || []);
        setEditForm({ name: data.name, icon: data.icon || '', category: data.category || 'CRM', slug_plural: data.slug_plural || '' });
      }
    } catch (error) {
      console.error('Error fetching object definition:', error);
    } finally {
      setLoading(false);
    }
  }, [objectDefinitionId]);

  const fetchRelationships = useCallback(async () => {
    try {
      const response = await apiRequest(`/crm/objects/${objectDefinitionId}/relationships?include_all=true`);
      if (response.ok) {
        const data = await response.json();
        setRelationships(data);
      }
    } catch (error) {
      console.error('Error fetching relationships:', error);
    }
  }, [objectDefinitionId]);

  useEffect(() => {
    fetchObjectDefinition();
    fetchRelationships();
  }, [fetchObjectDefinition, fetchRelationships]);

  // Dirty check — has anything changed from the loaded objectDef?
  const isDirty = objectDef && (editForm.name !== objectDef.name || editForm.icon !== (objectDef.icon || '') || editForm.category !== (objectDef.category || 'CRM') || editForm.slug_plural !== (objectDef.slug_plural || ''));

  const handleSaveObject = async () => {
    if (!isDirty || objectDef.is_system) return;
    setSaving(true);
    try {
      const response = await apiRequest(`/crm/objects/${objectDefinitionId}`, {
        method: 'PUT',
        body: JSON.stringify(editForm),
      });
      if (response.ok) {
        const updated = await response.json();
        setObjectDef(updated);
        showSuccess('Object updated');
        onObjectUpdated?.();
      } else {
        const err = await response.json();
        showError(err.detail || 'Failed to update object');
      }
    } catch (error) {
      showError('Error updating object');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteObject = async () => {
    try {
      const response = await apiRequest(`/crm/objects/${objectDefinitionId}`, {
        method: 'DELETE',
      });
      if (response.ok) {
        showSuccess('Object deleted successfully');
        onObjectDeleted?.();
      } else {
        const err = await response.json();
        showError(err.detail || 'Failed to delete object');
      }
    } catch (error) {
      showError('Error deleting object');
    }
    setShowDeleteConfirm(false);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = fields.findIndex((f) => f.id === active.id);
    const newIndex = fields.findIndex((f) => f.id === over.id);
    const newFields = arrayMove(fields, oldIndex, newIndex);
    setFields(newFields);

    const reorderData = newFields.map((f, idx) => ({ id: f.id, sort_order: idx }));
    try {
      await apiRequest('/crm/fields/reorder', {
        method: 'PUT',
        body: JSON.stringify(reorderData),
      });
    } catch (error) {
      console.error('Error reordering fields:', error);
      setFields(fields);
    }
  };

  const handleDeleteField = async (field) => {
    try {
      const response = await apiRequest(`/crm/fields/${field.id}`, {
        method: 'DELETE',
      });
      if (response.ok) {
        showSuccess(`Field "${field.name}" deleted`);
        setFields((prev) => prev.filter((f) => f.id !== field.id));
        setDeleteFieldConfirm(null);
      } else {
        const err = await response.json();
        showError(err.detail || 'Failed to delete field');
      }
    } catch (error) {
      showError('Error deleting field');
    }
  };

  const handleDeleteRelationship = async (rel) => {
    try {
      const response = await apiRequest(`/crm/relationships/${rel.id}`, { method: 'DELETE' });
      if (response.ok) {
        showSuccess('Relationship deleted');
        setRelationships((prev) => prev.filter((r) => r.id !== rel.id));
        setDeleteRelConfirm(null);
      } else {
        const err = await response.json();
        showError(err.detail || 'Failed to delete relationship');
      }
    } catch (error) {
      showError('Error deleting relationship');
    }
  };

  const handleCreateRelationship = async (relData) => {
    try {
      const response = await apiRequest('/crm/relationships', {
        method: 'POST',
        body: JSON.stringify(relData),
      });
      if (response.ok) {
        showSuccess('Relationship created');
        setShowAddRelationship(false);
        fetchRelationships();
      } else {
        const err = await response.json();
        showError(err.detail || 'Failed to create relationship');
      }
    } catch (error) {
      showError('Error creating relationship');
    }
  };

  // Handle Enter key in name input to save
  const handleNameKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      nameInputRef.current?.blur();
      handleSaveObject();
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-lg" />
            <div>
              <div className="h-6 w-40 bg-gray-200 dark:bg-gray-700 rounded mb-1" />
              <div className="h-3.5 w-24 bg-gray-100 dark:bg-gray-800 rounded" />
            </div>
          </div>
          <div className="h-px bg-gray-200 dark:bg-gray-700 mb-6" />
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 h-64" />
        </div>
      </div>
    );
  }

  if (!objectDef) return null;

  const Icon = ICON_MAP[objectDef.icon] || Database;
  const isSystem = objectDef.is_system;

  return (
    <div className="space-y-8">
      {/* ── Inline Editable Header ── */}
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-700/60 flex items-center justify-center flex-shrink-0 mt-0.5">
          <Icon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3">
            {isSystem ? (
              <h2
                className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate cursor-default"
                title="System object — name cannot be changed"
              >
                {objectDef.name}
              </h2>
            ) : (
              <input
                ref={nameInputRef}
                type="text"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                onKeyDown={handleNameKeyDown}
                onBlur={() => { if (isDirty) handleSaveObject(); }}
                className="text-lg font-semibold text-gray-900 dark:text-gray-100 bg-transparent border-0 border-b border-transparent hover:border-gray-200 dark:hover:border-gray-600 focus:border-brand-500 focus:ring-0 px-0 py-0 w-full max-w-xs transition-colors outline-none"
                placeholder="Object name"
              />
            )}
            {isDirty && !saving && (
              <button
                onClick={handleSaveObject}
                className="p-1.5 rounded-md text-brand-600 hover:bg-brand-50 dark:text-brand-400 dark:hover:bg-brand-900/20 transition-colors flex-shrink-0"
                title="Save changes"
              >
                <Save className="w-4 h-4" />
              </button>
            )}
            {saving && (
              <div className="flex items-center gap-1.5 text-xs text-gray-400">
                <div className="w-3 h-3 border-2 border-gray-300 border-t-brand-500 rounded-full animate-spin" />
                Saving
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 mt-1">
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {isSystem ? 'System object' : 'Custom object'}
            </span>
            <span className="text-gray-300 dark:text-gray-600">·</span>
            <span
              className="text-sm text-gray-400 dark:text-gray-500 font-mono"
              title={isSystem ? "System object — slug cannot be changed" : undefined}
            >{objectDef.slug}</span>
            <span className="text-gray-300 dark:text-gray-600">·</span>
            <div className="flex items-center gap-1">
              <span className="text-sm text-gray-400 dark:text-gray-500">plural:</span>
              <input
                type="text"
                value={editForm.slug_plural}
                onChange={(e) => setEditForm({ ...editForm, slug_plural: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })}
                onBlur={() => { if (isDirty) handleSaveObject(); }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); handleSaveObject(); } }}
                className="text-sm text-gray-500 dark:text-gray-400 font-mono bg-transparent border-0 border-b border-transparent hover:border-gray-200 dark:hover:border-gray-600 focus:border-brand-500 focus:ring-0 px-0 py-0 w-24 transition-colors outline-none"
                placeholder="e.g. companies"
                title="Plural slug used in URLs: /data/{plural}"
              />
            </div>
            {!isSystem && (
              <>
                <span className="text-gray-300 dark:text-gray-600">·</span>
                <div className="flex items-center gap-1">
                  <span className="text-sm text-gray-400 dark:text-gray-500">icon:</span>
                  <input
                    type="text"
                    value={editForm.icon}
                    onChange={(e) => setEditForm({ ...editForm, icon: e.target.value })}
                    onBlur={() => { if (isDirty) handleSaveObject(); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); handleSaveObject(); } }}
                    className="text-sm text-gray-500 dark:text-gray-400 font-mono bg-transparent border-0 border-b border-transparent hover:border-gray-200 dark:hover:border-gray-600 focus:border-brand-500 focus:ring-0 px-0 py-0 w-20 transition-colors outline-none"
                    placeholder="box"
                  />
                </div>
              </>
            )}
            <span className="text-gray-300 dark:text-gray-600">·</span>
            <div className="flex items-center gap-1">
              <span className="text-sm text-gray-400 dark:text-gray-500">category:</span>
              {isSystem ? (
                <span className="text-sm text-gray-500 dark:text-gray-400 font-mono">{objectDef.category || 'CRM'}</span>
              ) : (
                <select
                  value={editForm.category}
                  onChange={(e) => {
                    setEditForm({ ...editForm, category: e.target.value });
                    // Auto-save on change since selects don't have onBlur save UX
                    setTimeout(() => {
                      const newForm = { ...editForm, category: e.target.value };
                      if (newForm.category !== (objectDef.category || 'CRM') || newForm.name !== objectDef.name || newForm.icon !== (objectDef.icon || '') || newForm.slug_plural !== (objectDef.slug_plural || '')) {
                        setSaving(true);
                        apiRequest(`/crm/objects/${objectDefinitionId}`, {
                          method: 'PUT',
                          body: JSON.stringify(newForm),
                        }).then(async (response) => {
                          if (response.ok) {
                            const updated = await response.json();
                            setObjectDef(updated);
                            showSuccess('Object updated');
                            onObjectUpdated?.();
                          } else {
                            const err = await response.json();
                            showError(err.detail || 'Failed to update object');
                          }
                        }).catch(() => {
                          showError('Error updating object');
                        }).finally(() => {
                          setSaving(false);
                        });
                      }
                    }, 0);
                  }}
                  className="text-sm text-gray-500 dark:text-gray-400 bg-transparent border-0 border-b border-transparent hover:border-gray-200 dark:hover:border-gray-600 focus:border-brand-500 focus:ring-0 px-0 py-0 transition-colors outline-none cursor-pointer"
                >
                  {(organization?.settings?.navbar_manage_items_config?.categories || ['CRM']).map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              )}
            </div>
          </div>

        </div>
      </div>

      {/* ── Fields Section ── */}
      <div id="section-fields" className="scroll-mt-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
          <div className="px-5 py-3.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Fields</h3>
                <span className="text-xs text-gray-400 dark:text-gray-500">
                  ({fields.length})
                </span>
              </div>
              <button
                onClick={() => setFieldModal({ open: true, field: null })}
                className="text-sm text-brand-600 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300 font-medium flex items-center gap-1.5 transition-colors"
              >
                <Plus className="h-3.5 w-3.5" />
                Add Field
              </button>
            </div>
          </div>
          {fields.length === 0 ? (
            <div className="px-5 pb-6 pt-2 text-center">
              <Database className="h-8 w-8 text-gray-200 dark:text-gray-600 mx-auto mb-2" />
              <p className="text-sm text-gray-500 dark:text-gray-400">No fields defined yet</p>
              <button
                onClick={() => setFieldModal({ open: true, field: null })}
                className="mt-3 text-sm text-brand-600 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300 font-medium"
              >
                Add your first field
              </button>
            </div>
          ) : (
            <div className="border-t border-gray-100 dark:border-gray-700">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext items={fields.map((f) => f.id)} strategy={verticalListSortingStrategy}>
                  {fields.map((field) => (
                    <SortableFieldRow
                      key={field.id}
                      field={field}
                      isSystem={isSystem && field.is_system}
                      objectDefinitions={objectDefinitions}
                      onEdit={(f) => setFieldModal({ open: true, field: f })}
                      onDelete={(f) => setDeleteFieldConfirm(f)}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            </div>
          )}
        </div>
      </div>

      {/* ── Relationships Section ── */}
      <div id="section-relationships" className="scroll-mt-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
          <div className="px-5 py-3.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Relationships</h3>
                <span className="text-xs text-gray-400 dark:text-gray-500">
                  ({relationships.length})
                </span>
              </div>
              <button
                onClick={() => setShowAddRelationship(true)}
                className="text-sm text-brand-600 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300 font-medium flex items-center gap-1.5 transition-colors"
              >
                <Plus className="h-3.5 w-3.5" />
                Add Relationship
              </button>
            </div>
          </div>
          {relationships.length === 0 ? (
            <div className="px-5 pb-6 pt-2 text-center">
              <Link2 className="h-8 w-8 text-gray-200 dark:text-gray-600 mx-auto mb-2" />
              <p className="text-sm text-gray-500 dark:text-gray-400">No relationships defined yet</p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                Many-to-one relationships are created automatically when you add a relationship field.
              </p>
            </div>
          ) : (
            <div className="border-t border-gray-100 dark:border-gray-700">
              {relationships.map((rel) => {
                const relatedObj = rel.related_object_definition;
                const RelIcon = ICON_MAP[relatedObj?.icon] || Database;
                const isM2M = rel.relationship_type === 'many_to_many';
                return (
                  <div
                    key={rel.id}
                    className="flex items-center gap-3 px-4 py-3 border-b border-gray-50 dark:border-gray-700/50 last:border-b-0 group hover:bg-gray-50/50 dark:hover:bg-gray-700/20 transition-colors"
                  >
                    <div className="w-8 h-8 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 flex items-center justify-center flex-shrink-0">
                      <Link2 className="w-4 h-4 text-indigo-500 dark:text-indigo-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{rel.display_name}</span>
                        <ArrowRight className="w-3 h-3 text-gray-300 dark:text-gray-600" />
                        <div className="flex items-center gap-1.5">
                          <RelIcon className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
                          <span className="text-sm text-gray-600 dark:text-gray-400">{relatedObj?.name}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className={`text-[10px] font-medium uppercase tracking-wider px-1.5 py-0.5 rounded ${
                          isM2M
                            ? 'bg-purple-50 text-purple-600 dark:bg-purple-900/20 dark:text-purple-400'
                            : 'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400'
                        }`}>
                          {isM2M ? 'Many-to-Many' : 'Many-to-One'}
                        </span>
                        {rel.is_system && (
                          <span className="text-[10px] text-gray-400 dark:text-gray-500 flex items-center gap-0.5">
                            <Lock className="w-2.5 h-2.5" /> System
                          </span>
                        )}
                        {isM2M && rel.metadata_fields?.length > 0 && (
                          <span className="text-[10px] text-gray-400 dark:text-gray-500">
                            {rel.metadata_fields.length} metadata field{rel.metadata_fields.length !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                    </div>
                    {!rel.is_system && (
                      <button
                        onClick={() => setDeleteRelConfirm(rel)}
                        className="p-1.5 text-gray-300 hover:text-rose-500 dark:text-gray-600 dark:hover:text-rose-400 rounded-md opacity-0 group-hover:opacity-100 transition-all"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Danger Zone (custom objects only) ── */}
      {!isSystem && (
        <div className="pt-4 mt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">Delete this object</h4>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                This will archive the object, its fields, views, and all records.
              </p>
            </div>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="px-3.5 py-2 text-sm font-medium rounded-lg text-rose-600 dark:text-rose-400 border border-rose-200 dark:border-rose-800 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-colors flex items-center gap-1.5"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Delete
            </button>
          </div>
        </div>
      )}

      {/* ── Modals ── */}

      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="absolute inset-0" onClick={() => setShowDeleteConfirm(false)} />
          <div className="relative z-10 bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
            <div className="px-6 pt-6 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-rose-600 dark:text-rose-400" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Delete "{objectDef.name}"?
                </h2>
              </div>
            </div>
            <div className="px-6 pb-6">
              <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                This will archive the object definition along with all its fields, views, and records.
                This action can be reversed by an administrator.
              </p>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 text-sm font-medium rounded-lg bg-rose-600 text-white hover:bg-rose-700 transition-colors"
                  onClick={handleDeleteObject}
                >
                  Delete Object
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {deleteFieldConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="absolute inset-0" onClick={() => setDeleteFieldConfirm(null)} />
          <div className="relative z-10 bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
            <div className="px-6 pt-6 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-rose-600 dark:text-rose-400" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Delete "{deleteFieldConfirm.name}"?
                </h2>
              </div>
            </div>
            <div className="px-6 pb-6">
              <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                This will remove the field from this object. Existing record data for this field will be preserved but hidden.
              </p>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  onClick={() => setDeleteFieldConfirm(null)}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 text-sm font-medium rounded-lg bg-rose-600 text-white hover:bg-rose-700 transition-colors"
                  onClick={() => handleDeleteField(deleteFieldConfirm)}
                >
                  Delete Field
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {fieldModal.open && (
        <FieldDefinitionModal
          objectDefinitionId={objectDefinitionId}
          field={fieldModal.field}
          objectDefinitions={objectDefinitions}
          nextSortOrder={fields.length}
          onClose={() => setFieldModal({ open: false, field: null })}
          onSaved={() => {
            setFieldModal({ open: false, field: null });
            fetchObjectDefinition();
          }}
        />
      )}

      {deleteRelConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="absolute inset-0" onClick={() => setDeleteRelConfirm(null)} />
          <div className="relative z-10 bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
            <div className="px-6 pt-6 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-rose-600 dark:text-rose-400" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Delete relationship?
                </h2>
              </div>
            </div>
            <div className="px-6 pb-6">
              <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                This will remove the "{deleteRelConfirm.display_name}" relationship and all existing links between records. This cannot be undone.
              </p>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  onClick={() => setDeleteRelConfirm(null)}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 text-sm font-medium rounded-lg bg-rose-600 text-white hover:bg-rose-700 transition-colors"
                  onClick={() => handleDeleteRelationship(deleteRelConfirm)}
                >
                  Delete Relationship
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAddRelationship && (
        <AddRelationshipModal
          currentObjectDefinitionId={objectDefinitionId}
          objectDefinitions={objectDefinitions}
          onClose={() => setShowAddRelationship(false)}
          onCreate={handleCreateRelationship}
        />
      )}
    </div>
  );
}


/* ── Relationship diagram SVG components ── */
/* Brand emerald: 500=#10b981, 600=#059669, 700=#047857 */
function ManyToManyDiagram({ sourceName, targetName, isActive }) {
  const nodeColor = isActive ? '#059669' : '#d1d5db';
  const nodeFill = isActive ? '#ecfdf5' : '#f9fafb';
  const textColor = isActive ? '#047857' : '#9ca3af';
  return (
    <svg viewBox="0 0 240 160" className="w-full h-auto">
      <defs>
        <linearGradient id="m2m-line-grad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={nodeColor} />
          <stop offset="50%" stopColor={isActive ? '#34d399' : '#d1d5db'} />
          <stop offset="100%" stopColor={nodeColor} />
        </linearGradient>
      </defs>
      {/* Connection lines — cross pattern */}
      {[
        { x1: 78, y1: 48, x2: 162, y2: 48, delay: '0s' },
        { x1: 78, y1: 82, x2: 162, y2: 82, delay: '0.08s' },
        { x1: 78, y1: 115, x2: 162, y2: 115, delay: '0.12s' },
        { x1: 78, y1: 48, x2: 162, y2: 82, delay: '0.16s' },
        { x1: 78, y1: 82, x2: 162, y2: 48, delay: '0.2s' },
        { x1: 78, y1: 82, x2: 162, y2: 115, delay: '0.22s' },
        { x1: 78, y1: 115, x2: 162, y2: 82, delay: '0.24s' },
      ].map((line, i) => (
        <line key={i} x1={line.x1} y1={line.y1} x2={line.x2} y2={line.y2}
          stroke="url(#m2m-line-grad)" strokeWidth={isActive ? 1.5 : 1}
          strokeDasharray={isActive ? 'none' : '4,3'}
          opacity={isActive ? 0.45 : 0.25}
          style={{ transition: 'all 0.4s cubic-bezier(0.22,1,0.36,1)', transitionDelay: line.delay }}
        />
      ))}
      {/* Source nodes (left) */}
      {[48, 82, 115].map((y, i) => (
        <g key={`s${i}`} style={{ transition: 'all 0.3s ease', transitionDelay: `${i * 0.06}s` }}>
          <rect x="18" y={y - 14} width="60" height="28" rx="7"
            fill={nodeFill} stroke={nodeColor} strokeWidth="1.5"
            style={{ transition: 'all 0.4s ease' }}
          />
          <text x="48" y={y + 5} textAnchor="middle" fontSize="11" fontWeight="500"
            fill={textColor} style={{ transition: 'fill 0.3s ease' }}>
            {(sourceName || 'Source').slice(0, 7)}
          </text>
        </g>
      ))}
      {/* Target nodes (right) */}
      {[48, 82, 115].map((y, i) => (
        <g key={`t${i}`} style={{ transition: 'all 0.3s ease', transitionDelay: `${i * 0.06 + 0.1}s` }}>
          <rect x="162" y={y - 14} width="60" height="28" rx="7"
            fill={nodeFill} stroke={nodeColor} strokeWidth="1.5"
            style={{ transition: 'all 0.4s ease' }}
          />
          <text x="192" y={y + 5} textAnchor="middle" fontSize="11" fontWeight="500"
            fill={textColor} style={{ transition: 'fill 0.3s ease' }}>
            {(targetName || 'Target').slice(0, 7)}
          </text>
        </g>
      ))}
      {/* Center arrows */}
      <text x="120" y="70" textAnchor="middle" fontSize="14" fontWeight="600"
        fill={isActive ? '#059669' : '#d1d5db'} style={{ transition: 'fill 0.3s ease' }}>↔</text>
    </svg>
  );
}

function ManyToOneDiagram({ sourceName, targetName, isActive }) {
  const nodeColor = isActive ? '#059669' : '#d1d5db';
  const srcFill = isActive ? '#ecfdf5' : '#f9fafb';
  const tgtFill = isActive ? '#d1fae5' : '#f3f4f6';
  const textColor = isActive ? '#047857' : '#9ca3af';
  return (
    <svg viewBox="0 0 240 160" className="w-full h-auto">
      <defs>
        <marker id="m2o-arrow" viewBox="0 0 10 7" refX="9" refY="3.5" markerWidth="7" markerHeight="5" orient="auto-start-reverse">
          <polygon points="0 0, 10 3.5, 0 7" fill={nodeColor} style={{ transition: 'fill 0.3s ease' }} />
        </marker>
      </defs>
      {/* Connection lines — many converge to one */}
      {[48, 82, 115].map((y, i) => (
        <line key={i} x1="78" y1={y} x2="157" y2="82"
          stroke={nodeColor} strokeWidth={isActive ? 1.5 : 1}
          strokeDasharray={isActive ? 'none' : '4,3'} markerEnd="url(#m2o-arrow)"
          opacity={isActive ? 0.5 : 0.25}
          style={{ transition: 'all 0.4s cubic-bezier(0.22,1,0.36,1)', transitionDelay: `${i * 0.1}s` }}
        />
      ))}
      {/* Source nodes (many, left) */}
      {[48, 82, 115].map((y, i) => (
        <g key={`s${i}`} style={{ transition: 'all 0.3s ease', transitionDelay: `${i * 0.06}s` }}>
          <rect x="18" y={y - 14} width="60" height="28" rx="7"
            fill={srcFill} stroke={nodeColor} strokeWidth="1.5"
            style={{ transition: 'all 0.4s ease' }}
          />
          <text x="48" y={y + 5} textAnchor="middle" fontSize="11" fontWeight="500"
            fill={textColor} style={{ transition: 'fill 0.3s ease' }}>
            {(sourceName || 'Source').slice(0, 7)}
          </text>
        </g>
      ))}
      {/* Target node (one, right — larger) */}
      <g style={{ transition: 'all 0.3s ease', transitionDelay: '0.2s' }}>
        <rect x="158" y="58" width="68" height="48" rx="10"
          fill={tgtFill} stroke={nodeColor} strokeWidth="2"
          style={{ transition: 'all 0.4s ease' }}
        />
        <text x="192" y="87" textAnchor="middle" fontSize="12" fontWeight="600"
          fill={textColor} style={{ transition: 'fill 0.3s ease' }}>
          {(targetName || 'Target').slice(0, 7)}
        </text>
      </g>
      {/* Arrow symbol */}
      <text x="120" y="70" textAnchor="middle" fontSize="14" fontWeight="600"
        fill={isActive ? '#059669' : '#d1d5db'} style={{ transition: 'fill 0.3s ease' }}>→</text>
    </svg>
  );
}

/* ── Step indicator ── */
function StepIndicator({ currentStep, totalSteps }) {
  return (
    <div className="flex items-center gap-1.5">
      {Array.from({ length: totalSteps }, (_, i) => (
        <div key={i} className="relative overflow-hidden rounded-full"
          style={{
            width: i === currentStep ? 20 : 6,
            height: 6,
            backgroundColor: i <= currentStep ? '#059669' : '#e5e7eb',
            transition: 'all 0.4s cubic-bezier(0.22,1,0.36,1)',
          }}
        />
      ))}
    </div>
  );
}

function AddRelationshipModal({ currentObjectDefinitionId, objectDefinitions, onClose, onCreate }) {
  const [step, setStep] = useState(0);
  const [relType, setRelType] = useState('');
  const [targetObjDefId, setTargetObjDefId] = useState('');
  const [name, setName] = useState('');
  const [inverseName, setInverseName] = useState('');
  const [metadataFields, setMetadataFields] = useState([]);
  const [slideDir, setSlideDir] = useState('forward');
  const [isAnimating, setIsAnimating] = useState(false);
  const [entering, setEntering] = useState(true);
  const [objectSearch, setObjectSearch] = useState('');

  const otherObjects = (objectDefinitions || []).filter((od) => od.id !== currentObjectDefinitionId);
  const currentObj = (objectDefinitions || []).find((od) => od.id === currentObjectDefinitionId);
  const targetObj = otherObjects.find((od) => od.id === targetObjDefId);

  const totalSteps = relType === 'many_to_many' ? 4 : 3;

  // Entrance animation
  useEffect(() => {
    const t = setTimeout(() => setEntering(false), 20);
    return () => clearTimeout(t);
  }, []);

  // Auto-fill names when target changes
  useEffect(() => {
    if (targetObj && currentObj) {
      if (!name) setName(`${currentObj.name}s`);
      if (!inverseName) setInverseName(`${targetObj.name}s`);
    }
  }, [targetObjDefId]); // eslint-disable-line react-hooks/exhaustive-deps

  const goToStep = (nextStep) => {
    if (isAnimating) return;
    if (step === 1) setObjectSearch('');
    setSlideDir(nextStep > step ? 'forward' : 'back');
    setIsAnimating(true);
    setTimeout(() => {
      setStep(nextStep);
      setTimeout(() => setIsAnimating(false), 300);
    }, 10);
  };

  const addMetadataField = () => {
    setMetadataFields((prev) => [
      ...prev,
      { slug: '', name: '', field_type: 'text', sort_order: prev.length },
    ]);
  };

  const updateMetadataField = (index, updates) => {
    setMetadataFields((prev) => prev.map((f, i) => i === index ? { ...f, ...updates } : f));
  };

  const removeMetadataField = (index) => {
    setMetadataFields((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    if (!targetObjDefId) return;
    onCreate({
      source_object_definition_id: currentObjectDefinitionId,
      target_object_definition_id: targetObjDefId,
      relationship_type: relType,
      name: name || `${currentObj?.name || 'Record'}s`,
      inverse_name: inverseName || `${targetObj?.name || 'Record'}s`,
      metadata_fields: relType === 'many_to_many' ? metadataFields.filter((f) => f.slug && f.name) : [],
    });
  };

  const canProceed = () => {
    if (step === 0) return !!relType;
    if (step === 1) return !!targetObjDefId;
    return true;
  };

  const srcName = currentObj?.name || 'this object';

  const tgtName = targetObj?.name || 'Target';
  const confirmStepIndex = relType === 'many_to_many' ? 3 : 2;

  const stepTitles = [
    'Choose relationship type',
    `Connect ${srcName} to...`,
    'Define link fields',
    'Review & confirm',
  ];

  const stepDescriptions = [
    `From a ${srcName} record's perspective, how should it connect to the other object?`,
    `Select the object type that ${srcName} records will link to.`,
    `Add optional fields that describe each link (e.g. job title, start date).`,
    `Here's what will be created. Make sure everything looks right.`,
  ];

  // For many_to_one, skip step 2 (link fields) — map step indices
  const activeStepTitles = relType === 'many_to_many'
    ? stepTitles
    : [stepTitles[0], stepTitles[1], stepTitles[3]];
  const activeStepDescriptions = relType === 'many_to_many'
    ? stepDescriptions
    : [stepDescriptions[0], stepDescriptions[1], stepDescriptions[3]];

  const baseInput = "w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 placeholder:text-gray-400";

  const IconForObject = (obj) => {
    const IconComp = ICON_MAP[obj?.icon] || Box;
    return IconComp;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      style={{ opacity: entering ? 0 : 1, transition: 'opacity 0.2s ease' }}>
      <div className="absolute inset-0" onClick={onClose} />
      <div className="relative z-10 bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full mx-4 overflow-hidden flex flex-col"
        style={{
          maxWidth: step === 0 ? 680 : 520,
          maxHeight: '85vh',
          transform: entering ? 'scale(0.95) translateY(10px)' : 'scale(1) translateY(0)',
          transition: 'transform 0.35s cubic-bezier(0.22,1,0.36,1), opacity 0.25s ease, max-width 0.4s cubic-bezier(0.22,1,0.36,1)',
        }}>
        {/* Header */}
        <div className="px-6 pt-5 pb-4">
          <div className="flex items-center justify-between mb-3">
            <StepIndicator currentStep={step} totalSteps={totalSteps} />
            <button onClick={onClose}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:text-gray-300 dark:hover:bg-gray-800 transition-all">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100"
            style={{ transition: 'all 0.3s ease' }} key={`title-${step}`}>
            {activeStepTitles[step]}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" key={`desc-${step}`}>
            {activeStepDescriptions[step]}
          </p>
        </div>

        {/* Step content with slide transitions */}
        <div className="relative flex-1 overflow-y-auto" style={{ minHeight: step === 0 ? 340 : step === confirmStepIndex ? 220 : 200 }}>
          <div className="px-6 pb-5"
            style={{
              transition: 'transform 0.35s cubic-bezier(0.22,1,0.36,1), opacity 0.25s ease',
              transform: isAnimating
                ? `translateX(${slideDir === 'forward' ? '-20px' : '20px'})`
                : 'translateX(0)',
              opacity: isAnimating ? 0 : 1,
            }}>

            {/* Step 0: Relationship Type */}
            {step === 0 && (
              <div className="grid grid-cols-2 gap-4">
                {/* Many-to-Many card */}
                <button type="button" onClick={() => setRelType('many_to_many')}
                  className={`text-left rounded-xl border-2 p-4 pb-5 transition-all duration-300 flex flex-col ${
                    relType === 'many_to_many'
                      ? 'border-brand-400 bg-brand-50/30 dark:border-brand-600 dark:bg-brand-900/10 shadow-md shadow-brand-100/50 dark:shadow-none'
                      : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-50/50 dark:hover:bg-gray-800'
                  }`}>
                  {/* Diagram area */}
                  <div className={`rounded-lg p-3 mb-4 transition-colors duration-300 ${
                    relType === 'many_to_many' ? 'bg-brand-50 dark:bg-brand-900/20' : 'bg-gray-50 dark:bg-gray-800'
                  }`}>
                    <ManyToManyDiagram
                      sourceName={currentObj?.name}
                      targetName="Target"
                      isActive={relType === 'many_to_many'}
                    />
                  </div>
                  {/* Text content */}
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className={`text-sm font-semibold transition-colors ${
                      relType === 'many_to_many' ? 'text-brand-700 dark:text-brand-400' : 'text-gray-800 dark:text-gray-200'
                    }`}>
                      Many-to-Many
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                    Both sides can link to multiple records. E.g. People ↔ Companies, Tags ↔ Items.
                  </p>
                </button>

                {/* Many-to-One card */}
                <button type="button" onClick={() => setRelType('many_to_one')}
                  className={`text-left rounded-xl border-2 p-4 pb-5 transition-all duration-300 flex flex-col ${
                    relType === 'many_to_one'
                      ? 'border-brand-400 bg-brand-50/30 dark:border-brand-600 dark:bg-brand-900/10 shadow-md shadow-brand-100/50 dark:shadow-none'
                      : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-50/50 dark:hover:bg-gray-800'
                  }`}>
                  {/* Diagram area */}
                  <div className={`rounded-lg p-3 mb-4 transition-colors duration-300 ${
                    relType === 'many_to_one' ? 'bg-brand-50 dark:bg-brand-900/20' : 'bg-gray-50 dark:bg-gray-800'
                  }`}>
                    <ManyToOneDiagram
                      sourceName={currentObj?.name}
                      targetName="Target"
                      isActive={relType === 'many_to_one'}
                    />
                  </div>
                  {/* Text content */}
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className={`text-sm font-semibold transition-colors ${
                      relType === 'many_to_one' ? 'text-brand-700 dark:text-brand-400' : 'text-gray-800 dark:text-gray-200'
                    }`}>
                      Many-to-One
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                    Many {srcName} records point to one target. Adds a lookup field. E.g. Deals → Company.
                  </p>
                </button>
              </div>
            )}

            {/* Step 1: Target Object — searchable list */}
            {step === 1 && (() => {
              const filtered = otherObjects.filter((od) =>
                od.name.toLowerCase().includes(objectSearch.toLowerCase())
              );
              // Put selected item first if it exists and isn't in filtered results
              const selectedFirst = targetObjDefId && !filtered.find(od => od.id === targetObjDefId)
                ? [otherObjects.find(od => od.id === targetObjDefId), ...filtered].filter(Boolean)
                : filtered;

              return (
                <div className="space-y-3">
                  {/* Search input */}
                  {otherObjects.length > 5 && (
                    <div className="relative">
                      <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" viewBox="0 0 20 20" fill="none">
                        <circle cx="8.5" cy="8.5" r="5.5" stroke="currentColor" strokeWidth="1.5" />
                        <path d="M12.5 12.5L17 17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                      </svg>
                      <input
                        type="text"
                        value={objectSearch}
                        onChange={(e) => setObjectSearch(e.target.value)}
                        placeholder="Search objects..."
                        className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 placeholder:text-gray-400"
                      />
                    </div>
                  )}

                  {/* Scrollable object list */}
                  <div className="space-y-1.5 max-h-[240px] overflow-y-auto pr-1" style={{ scrollbarWidth: 'thin' }}>
                    {selectedFirst.length === 0 ? (
                      <div className="text-center py-8 text-sm text-gray-400 dark:text-gray-500">
                        {otherObjects.length === 0
                          ? 'No other objects available. Create another object first.'
                          : 'No objects match your search.'}
                      </div>
                    ) : (
                      selectedFirst.map((od) => {
                        const ObjIcon = IconForObject(od);
                        const isSelected = targetObjDefId === od.id;
                        return (
                          <button key={od.id} type="button"
                            onClick={() => { setTargetObjDefId(od.id); setName(''); setInverseName(''); }}
                            className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-lg border transition-all duration-200 ${
                              isSelected
                                ? 'border-brand-400 bg-brand-50/50 dark:border-brand-600 dark:bg-brand-900/10'
                                : 'border-transparent bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800'
                            }`}>
                            <div className={`w-7 h-7 rounded-md flex items-center justify-center transition-colors ${
                              isSelected
                                ? 'bg-brand-100 text-brand-600 dark:bg-brand-900/40 dark:text-brand-400'
                                : 'bg-gray-200/60 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                            }`}>
                              <ObjIcon className="w-3.5 h-3.5" />
                            </div>
                            <span className={`text-sm font-medium flex-1 text-left transition-colors ${
                              isSelected ? 'text-gray-900 dark:text-gray-100' : 'text-gray-700 dark:text-gray-300'
                            }`}>
                              {od.name}
                            </span>
                            {isSelected && (
                              <div className="w-5 h-5 rounded-full flex items-center justify-center bg-brand-500">
                                <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                                  <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </div>
                            )}
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Step 2: Metadata fields (many_to_many only) */}
            {step === 2 && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs text-gray-400 dark:text-gray-500">
                    These fields describe each link between a {currentObj?.name} and a {targetObj?.name} record.
                  </p>
                  <button onClick={addMetadataField}
                    className="text-xs font-medium flex items-center gap-1 px-2.5 py-1 rounded-lg text-brand-600 hover:text-brand-700 dark:text-brand-400 hover:bg-brand-50 dark:hover:bg-brand-900/20 transition-colors">
                    <Plus className="w-3 h-3" /> Add field
                  </button>
                </div>
                {metadataFields.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-10 text-center">
                    <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-3">
                      <Database className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 font-medium mb-1">No link fields</p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 max-w-[240px] leading-relaxed">
                      This is optional. You can add fields like "Job Title" or "Start Date" to capture details about each link.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {metadataFields.map((mf, idx) => (
                      <div key={idx}
                        className="flex items-center gap-2 p-2 rounded-lg bg-gray-50 dark:bg-gray-800/60 border border-gray-100 dark:border-gray-700"
                        style={{ animation: 'addRelFadeIn 0.25s ease forwards' }}>
                        <div className="w-5 h-5 rounded flex items-center justify-center bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-[10px] font-bold flex-shrink-0">
                          {idx + 1}
                        </div>
                        <input type="text" value={mf.name}
                          onChange={(e) => {
                            const slug = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_').replace(/_+/g, '_');
                            updateMetadataField(idx, { name: e.target.value, slug });
                          }}
                          placeholder="Field name"
                          className="flex-1 px-2.5 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 placeholder:text-gray-400"
                        />
                        <select value={mf.field_type}
                          onChange={(e) => updateMetadataField(idx, { field_type: e.target.value })}
                          className="px-2 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500">
                          <option value="text">Text</option>
                          <option value="number">Number</option>
                          <option value="date">Date</option>
                          <option value="boolean">Boolean</option>
                          <option value="select">Select</option>
                        </select>
                        <button onClick={() => removeMetadataField(idx)}
                          className="p-1 text-gray-300 hover:text-rose-500 dark:text-gray-600 dark:hover:text-rose-400 transition-colors">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Confirmation step */}
            {step === confirmStepIndex && (
              <div className="space-y-4" style={{ animation: 'addRelFadeIn 0.3s ease forwards' }}>
                {/* Relationship visual header */}
                <div className="flex items-center justify-center gap-3 py-2">
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-800">
                    {(() => { const I = IconForObject(currentObj); return <I className="w-4 h-4 text-gray-500 dark:text-gray-400" />; })()}
                    <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{srcName}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-6 h-px bg-brand-400 dark:bg-brand-600" />
                    <span className="text-xs font-bold text-brand-600 dark:text-brand-400">
                      {relType === 'many_to_many' ? '↔' : '→'}
                    </span>
                    <div className="w-6 h-px bg-brand-400 dark:bg-brand-600" />
                  </div>
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-800">
                    {(() => { const I = IconForObject(targetObj); return <I className="w-4 h-4 text-gray-500 dark:text-gray-400" />; })()}
                    <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{tgtName}</span>
                  </div>
                </div>

                {/* Summary details */}
                <div className="rounded-xl bg-gray-50 dark:bg-gray-800/60 border border-gray-100 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
                  <div className="flex items-center justify-between px-4 py-3 text-sm">
                    <span className="text-gray-500 dark:text-gray-400">Type</span>
                    <span className="font-medium text-gray-800 dark:text-gray-200">
                      {relType === 'many_to_many' ? 'Many-to-Many' : 'Many-to-One'}
                    </span>
                  </div>
                  {relType === 'many_to_many' && metadataFields.filter(f => f.slug && f.name).length > 0 && (
                    <div className="flex items-center justify-between px-4 py-3 text-sm">
                      <span className="text-gray-500 dark:text-gray-400">Link fields</span>
                      <div className="flex flex-wrap gap-1.5 justify-end">
                        {metadataFields.filter(f => f.slug && f.name).map((f, i) => (
                          <span key={i} className="px-2 py-0.5 rounded-md text-xs font-medium bg-gray-200/70 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                            {f.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Editable display names */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                      Label on {tgtName} records
                    </label>
                    <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                      placeholder={`${srcName}s`} className={baseInput} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                      Label on {srcName} records
                    </label>
                    <input type="text" value={inverseName} onChange={(e) => setInverseName(e.target.value)}
                      placeholder={`${tgtName}s`} className={baseInput} />
                  </div>
                </div>

                {/* Explanation */}
                <p className="text-xs text-gray-400 dark:text-gray-500 leading-relaxed">
                  {relType === 'many_to_many'
                    ? `A "${inverseName || tgtName + 's'}" button will appear on ${srcName} records, and a "${name || srcName + 's'}" button on ${tgtName} records.`
                    : `A "${inverseName || tgtName + 's'}" button will appear on ${srcName} records, linking each to one ${tgtName}.`
                  }
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer with navigation */}
        <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">
          <div>
            {step > 0 && (
              <button onClick={() => goToStep(step - 1)}
                className="text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors flex items-center gap-1">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="rotate-180">
                  <path d="M5 3L9 7L5 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Back
              </button>
            )}
          </div>
          <div className="flex gap-3">
            <button className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              onClick={onClose}>
              Cancel
            </button>
            {step < totalSteps - 1 ? (
              <button onClick={() => canProceed() && goToStep(step + 1)}
                disabled={!canProceed()}
                className={`px-5 py-2 text-sm font-medium rounded-lg transition-all duration-300 flex items-center gap-1.5 ${
                  canProceed()
                    ? 'bg-brand-600 text-white hover:bg-brand-700 shadow-sm shadow-brand-200 dark:shadow-none'
                    : 'bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-600 cursor-not-allowed'
                }`}>
                Continue
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M5 3L9 7L5 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            ) : (
              <button onClick={handleSubmit} disabled={!targetObjDefId}
                className="px-5 py-2 text-sm font-medium rounded-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed bg-brand-600 text-white hover:bg-brand-700 shadow-sm shadow-brand-200 dark:shadow-none">
                Create Relationship
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Keyframe animation for fade-in elements */}
      <style>{`
        @keyframes addRelFadeIn {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
